import { apiClient } from "@/services/apiClient";

export interface BusinessUnit {
  businessUnitId: number;
  guid: string;
  code: string;
  name: string;
}

export interface UpsertBusinessUnitRequest {
  code: string;
  name: string;
}

export interface Department {
  departmentId: number;
  guid: string;
  businessUnitId: number;
  businessUnitCode: string;
  name: string;
}

export interface UpsertDepartmentRequest {
  businessUnitId: number;
  name: string;
}

export interface RecordLocation {
  locationId: number;
  guid: string;
  name: string;
  locationType: string;
}

export interface UpsertLocationRequest {
  name: string;
  locationType: string;
}

export interface RetentionLabel {
  retentionLabelId: number;
  guid: string;
  name: string;
  retentionYears?: number | null;
  requiresClassification: boolean;
  description?: string | null;
}

export interface UpsertRetentionLabelRequest {
  name: string;
  retentionYears?: number | null;
  requiresClassification?: boolean;
  description?: string | null;
}

export interface UserAccess {
  userAccessId: number;
  guid: string;
  userEmail: string;
  businessUnitId: number;
  businessUnitCode: string;
  departmentId: number;
  departmentName: string;
  isDirector: boolean;
}

export interface UpsertUserAccessRequest {
  userEmail: string;
  businessUnitId: number;
  departmentId: number;
  isDirector: boolean;
}

const BASE = "/api/physical-records/admin";

// Business Units
export const getBusinessUnits = async (): Promise<BusinessUnit[]> =>
  (await apiClient.get<BusinessUnit[]>(`${BASE}/business-units`)).data;
export const createBusinessUnit = async (req: UpsertBusinessUnitRequest): Promise<BusinessUnit> =>
  (await apiClient.post<BusinessUnit>(`${BASE}/business-units`, req)).data;
export const updateBusinessUnit = async (id: number, req: UpsertBusinessUnitRequest): Promise<void> => {
  await apiClient.put(`${BASE}/business-units/${id}`, req);
};
export const deleteBusinessUnit = async (id: number): Promise<void> => {
  await apiClient.delete(`${BASE}/business-units/${id}`);
};

// Departments
export const getDepartments = async (businessUnitId?: number): Promise<Department[]> =>
  (await apiClient.get<Department[]>(`${BASE}/departments`, { params: { businessUnitId } })).data;
export const createDepartment = async (req: UpsertDepartmentRequest): Promise<Department> =>
  (await apiClient.post<Department>(`${BASE}/departments`, req)).data;
export const updateDepartment = async (id: number, req: UpsertDepartmentRequest): Promise<void> => {
  await apiClient.put(`${BASE}/departments/${id}`, req);
};
export const deleteDepartment = async (id: number): Promise<void> => {
  await apiClient.delete(`${BASE}/departments/${id}`);
};

// Locations
export const getLocations = async (): Promise<RecordLocation[]> =>
  (await apiClient.get<RecordLocation[]>(`${BASE}/locations`)).data;
export const createLocation = async (req: UpsertLocationRequest): Promise<RecordLocation> =>
  (await apiClient.post<RecordLocation>(`${BASE}/locations`, req)).data;
export const updateLocation = async (id: number, req: UpsertLocationRequest): Promise<void> => {
  await apiClient.put(`${BASE}/locations/${id}`, req);
};
export const deleteLocation = async (id: number): Promise<void> => {
  await apiClient.delete(`${BASE}/locations/${id}`);
};

// Retention Labels
export const getRetentionLabels = async (): Promise<RetentionLabel[]> =>
  (await apiClient.get<RetentionLabel[]>(`${BASE}/retention-labels`)).data;
export const createRetentionLabel = async (req: UpsertRetentionLabelRequest): Promise<RetentionLabel> =>
  (await apiClient.post<RetentionLabel>(`${BASE}/retention-labels`, req)).data;
export const updateRetentionLabel = async (id: number, req: UpsertRetentionLabelRequest): Promise<void> => {
  await apiClient.put(`${BASE}/retention-labels/${id}`, req);
};
export const deleteRetentionLabel = async (id: number): Promise<void> => {
  await apiClient.delete(`${BASE}/retention-labels/${id}`);
};

// User Access
export const getUserAccess = async (): Promise<UserAccess[]> =>
  (await apiClient.get<UserAccess[]>(`${BASE}/user-access`)).data;
export const createUserAccess = async (req: UpsertUserAccessRequest): Promise<UserAccess> =>
  (await apiClient.post<UserAccess>(`${BASE}/user-access`, req)).data;
export const deleteUserAccess = async (id: number): Promise<void> => {
  await apiClient.delete(`${BASE}/user-access/${id}`);
};

// ── Boxes & Files ────────────────────────────────────────────────────────

export interface Box {
  boxId: number;
  guid: string;
  boxNumber: string;
  boxName: string;
  locationId: number;
  locationName: string;
  departmentId: number;
  departmentName: string;
  contentStartDate?: string | null;
  contentEndDate?: string | null;
  description?: string | null;
  keywords?: string | null;
  retentionLabelId: number;
  retentionLabelName: string;
  offsiteId?: string | null;
  createUser: string;
  createDt: string;
  transferUser?: string | null;
  transferDt?: string | null;
  fileCount: number;
}

export interface CreateBoxRequest {
  boxName: string;
  locationId: number;
  departmentId: number;
  contentStartDate: string;
  contentEndDate: string;
  description: string;
  keywords: string;
  retentionLabelId: number;
  offsiteId?: string | null;
}

export type UpdateBoxRequest = CreateBoxRequest;

export interface RecordFile {
  fileId: number;
  guid: string;
  fileNumber: string;
  fileName: string;
  description?: string | null;
  locationId: number;
  locationName: string;
  departmentId: number;
  departmentName: string;
  keywords?: string | null;
  contentStartDate?: string | null;
  contentEndDate?: string | null;
  retentionLabelId: number;
  retentionLabelName: string;
  contractParties?: string | null;
  offsiteId?: string | null;
  offsiteStorageId?: string | null;
  boxId?: number | null;
  boxNumber?: string | null;
  createUser: string;
  createDt: string;
  transferUser?: string | null;
  transferDt?: string | null;
}

export interface CreateFileRequest {
  fileName: string;
  description: string;
  locationId: number;
  departmentId: number;
  keywords: string;
  contentStartDate: string;
  contentEndDate: string;
  retentionLabelId: number;
  contractParties?: string | null;
  offsiteId?: string | null;
  offsiteStorageId?: string | null;
  boxId?: number | null;
}

export interface UpdateFileRequest {
  fileName: string;
  description: string;
  locationId: number;
  departmentId: number;
  keywords: string;
  contentStartDate: string;
  contentEndDate: string;
  contractParties?: string | null;
  offsiteId?: string | null;
  offsiteStorageId?: string | null;
}

const RECORDS_BASE = "/api/physical-records";

export const getBoxes = async (): Promise<Box[]> =>
  (await apiClient.get<Box[]>(`${RECORDS_BASE}/boxes`)).data;
export const getBox = async (boxId: number): Promise<Box> =>
  (await apiClient.get<Box>(`${RECORDS_BASE}/boxes/${boxId}`)).data;
export const createBox = async (req: CreateBoxRequest): Promise<Box> =>
  (await apiClient.post<Box>(`${RECORDS_BASE}/boxes`, req)).data;
export const updateBox = async (boxId: number, req: UpdateBoxRequest): Promise<void> => {
  await apiClient.put(`${RECORDS_BASE}/boxes/${boxId}`, req);
};
export const getFilesForBox = async (boxId: number): Promise<RecordFile[]> =>
  (await apiClient.get<RecordFile[]>(`${RECORDS_BASE}/boxes/${boxId}/files`)).data;

export const getFile = async (fileId: number): Promise<RecordFile> =>
  (await apiClient.get<RecordFile>(`${RECORDS_BASE}/files/${fileId}`)).data;
export const createFile = async (req: CreateFileRequest): Promise<RecordFile> =>
  (await apiClient.post<RecordFile>(`${RECORDS_BASE}/files`, req)).data;
export const updateFile = async (fileId: number, req: UpdateFileRequest): Promise<void> => {
  await apiClient.put(`${RECORDS_BASE}/files/${fileId}`, req);
};
export const assignFileToBox = async (fileId: number, boxId: number): Promise<void> => {
  await apiClient.post(`${RECORDS_BASE}/files/${fileId}/assign-box`, { boxId });
};
export const removeFileFromBox = async (fileId: number): Promise<void> => {
  await apiClient.post(`${RECORDS_BASE}/files/${fileId}/remove-from-box`);
};

// ── Unified Physical Objects (migration-validation surface) ─────────────

export type PhysicalObjectType = "Box" | "File";

export interface PhysicalObjectSearch {
  id: number;
  objectType: PhysicalObjectType;
  physicalId?: string | null;
  name: string;
  description?: string | null;
  directoryId?: number | null;
  directoryName?: string | null;
  homeLocation?: string | null;
  currentLocation?: string | null;
  keywords?: string | null;
  contractParties?: string | null;
  legacyFileId?: string | null;
  offsiteId?: string | null;
  offsiteStorageId?: string | null;
  sourceCreatedDate?: string | null;
  sourceCreatedBy?: string | null;
  sourceModifiedDate?: string | null;
  sourceModifiedBy?: string | null;
  contentStartDate?: string | null;
  contentEndDate?: string | null;
  retentionLabel?: string | null;
  dispositionStatus?: string | null;
  childCount: number;
}

export interface PhysicalObjectDetail extends PhysicalObjectSearch {
  guid: string;
  classificationId?: number | null;
  classificationCode?: string | null;
  classificationName?: string | null;
  classificationRsi?: string | null;
  classificationFileStatus?: string | null;
  classificationEssential?: string | null;
  classificationStorage?: string | null;
  classificationCyclePeriod?: number | null;
  recordDate?: string | null;
  recordStatus?: string | null;
  statusDate?: string | null;
  nextReviewDate?: string | null;
  description?: string | null;
  parentPhysicalObjectId?: number | null;
  parentPhysicalId?: string | null;
  parentName?: string | null;
  directoryPath?: string | null;
  departmentId?: number | null;
  homeLocationId?: number | null;
  currentLocationId?: number | null;
  homeLocationRaw?: string | null;
  currentLocationRaw?: string | null;
  keywords?: string | null;
  contractParties?: string | null;
  legacyFileId?: string | null;
  lastTransferUser?: string | null;
  lastTransferDate?: string | null;
  offsiteId?: string | null;
  offsiteStorageId?: string | null;
  sourceCreatedDate?: string | null;
  sourceCreatedBy?: string | null;
  sourceModifiedDate?: string | null;
  sourceModifiedBy?: string | null;
  legacySystem?: string | null;
  legacyNodeId?: number | null;
  legacyParentNodeId?: number | null;
  legacySubtype?: number | null;
  legacyMediaTypeId?: number | null;
  legacyContainerNumber?: string | null;
  legacyBoxNodeId?: number | null;
  legacyDeletedCode?: number | null;
  legacyDeletedCodeMeaningUnvalidated: boolean;
}

export interface PhysicalRecordsDirectory {
  directoryId: number;
  parentDirectoryId?: number | null;
  classificationId?: number | null;
  classificationCode?: string | null;
  classificationName?: string | null;
  classificationRsi?: string | null;
  name: string;
  description?: string | null;
  legacyNodeId?: number | null;
  legacyParentNodeId?: number | null;
  directChildDirectoryCount: number;
  directObjectCount: number;
  recursiveObjectCount: number;
  boxCount: number;
  fileCount: number;
  objectCount: number;
  hasChildren: boolean;
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface PhysicalRecordsClassification {
  classificationId: number;
  code: string;
  name: string;
  rsi?: string | null;
  fileStatus?: string | null;
  essential?: string | null;
  storage?: string | null;
  disposition?: string | null;
  keywords?: string | null;
  subject?: string | null;
  dispositionAuthority?: string | null;
  cyclePeriod?: number | null;
  closedFlag?: number | null;
}

const normalizePhysicalObjectType = (value: PhysicalObjectType | number): PhysicalObjectType =>
  value === 1 || value === "Box" ? "Box" : "File";

const UNIFIED_BASE = "/api/physical-records";

export const getPhysicalRecordsDirectoryRoot = async (): Promise<PhysicalRecordsDirectory> =>
  (await apiClient.get<PhysicalRecordsDirectory>(`${UNIFIED_BASE}/directories/root`)).data;

export const searchPhysicalObjects = async (params?: {
  text?: string;
  physicalId?: string;
  recordNumber?: string;
  objectType?: PhysicalObjectType;
  directoryId?: number;
  contentDateFrom?: string;
  contentDateTo?: string;
  description?: string;
  keywords?: string;
  contractParties?: string;
  fileId?: string;
  legacyBoxNumber?: string;
  offsiteId?: string;
  offsiteStorageId?: string;
  homeLocation?: string;
  currentLocation?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
}): Promise<PagedResult<PhysicalObjectSearch>> =>
  (await apiClient.get<PagedResult<PhysicalObjectSearch>>(`${UNIFIED_BASE}/objects`, { params })).data;

export const getPhysicalObject = async (id: number | string): Promise<PhysicalObjectDetail> =>
  (() => {
    const path = typeof id === "string"
      ? `by-physical-id/${encodeURIComponent(id.trim().toUpperCase())}` : String(id);
    const response = apiClient.get<PhysicalObjectDetail>(`${UNIFIED_BASE}/objects/${path}`);
    return response.then(({ data }) => ({ ...data, objectType: normalizePhysicalObjectType(data.objectType as PhysicalObjectType | number) }));
  })();

export const getPhysicalRecordsClassifications = async (search?: string): Promise<PhysicalRecordsClassification[]> =>
  (await apiClient.get<PhysicalRecordsClassification[]>(`${UNIFIED_BASE}/classifications`, { params: { search } })).data;

export const updatePhysicalObjectKeyInformation = async (
  id: number,
  request: { name: string; description?: string | null },
): Promise<void> => {
  await apiClient.put(`${UNIFIED_BASE}/objects/${id}/key-information`, request);
};

export const updatePhysicalObjectPhysicalDetails = async (
  id: number,
  request: {
    homeLocationRaw?: string | null;
    currentLocationRaw?: string | null;
    keywords?: string | null;
    contentStartDate?: string | null;
    contentEndDate?: string | null;
    contractParties?: string | null;
    offsiteId?: string | null;
    offsiteStorageId?: string | null;
  },
): Promise<void> => {
  await apiClient.put(`${UNIFIED_BASE}/objects/${id}/physical-details`, request);
};

export const setPhysicalObjectClassification = async (id: number, classificationId: number | null): Promise<void> => {
  await apiClient.put(`${UNIFIED_BASE}/objects/${id}/classification`, { classificationId });
};

export const updatePhysicalObjectRecordsDetail = async (
  id: number,
  request: { recordDate?: string | null; recordStatus?: string | null; statusDate?: string | null; nextReviewDate?: string | null },
): Promise<void> => {
  await apiClient.put(`${UNIFIED_BASE}/objects/${id}/records-detail`, request);
};

export const movePhysicalFileToBox = async (fileId: number, boxId: number): Promise<void> => {
  await apiClient.put(`${UNIFIED_BASE}/objects/${fileId}/box`, { boxId });
};

export const removePhysicalFileFromBox = async (fileId: number): Promise<void> => {
  await apiClient.delete(`${UNIFIED_BASE}/objects/${fileId}/box`);
};

export const getPhysicalObjectChildren = async (
  id: number,
  params?: { page?: number; pageSize?: number },
): Promise<PagedResult<PhysicalObjectSearch>> =>
  apiClient.get<PagedResult<PhysicalObjectSearch>>(`${UNIFIED_BASE}/objects/${id}/children`, { params }).then(({ data }) => ({ ...data, items: data.items.map((item) => ({ ...item, objectType: normalizePhysicalObjectType(item.objectType as PhysicalObjectType | number) })) }));

export const getPhysicalObjectContents = async (
  id: number,
  params?: { text?: string; page?: number; pageSize?: number; sort?: string },
): Promise<PagedResult<PhysicalObjectSearch>> =>
  apiClient.get<PagedResult<PhysicalObjectSearch>>(`${UNIFIED_BASE}/objects/${id}/contents`, { params }).then(({ data }) => ({ ...data, items: data.items.map((item) => ({ ...item, objectType: normalizePhysicalObjectType(item.objectType as PhysicalObjectType | number) })) }));

export interface PhysicalObjectAuditEvent {
  auditEventId: number;
  eventCode: string;
  eventLabel?: string | null;
  eventDate: string;
  performerDisplayName?: string | null;
  performerRaw?: string | null; // Added to support backend fallback
  legacyPerformerId?: number | null; // Added for expanded audit details
  detailKey?: string | null;
  detailValue?: string | null;
  legacyValueKey?: string | null;
  legacyValue1?: string | null;
  legacyValue2?: string | null;
}

export const getPhysicalObjectAudit = async (
  id: number,
  params?: { page?: number; pageSize?: number },
): Promise<PagedResult<PhysicalObjectAuditEvent>> =>
  (await apiClient.get<PagedResult<PhysicalObjectAuditEvent>>(`${UNIFIED_BASE}/objects/${id}/audit`, { params })).data;

export const getPhysicalRecordsDirectories = async (params?: {
  parentDirectoryId?: number;
  page?: number;
  pageSize?: number;
  text?: string;
}): Promise<PagedResult<PhysicalRecordsDirectory>> =>
  (await apiClient.get<PagedResult<PhysicalRecordsDirectory>>(`${UNIFIED_BASE}/directories`, { params })).data;

export const getPhysicalRecordsDirectoryChildren = async (
  directoryId: number,
  params?: { page?: number; pageSize?: number; text?: string },
): Promise<PagedResult<PhysicalRecordsDirectory>> =>
  (await apiClient.get<PagedResult<PhysicalRecordsDirectory>>(`${UNIFIED_BASE}/directories/${directoryId}/children`, { params })).data;

export const getPhysicalRecordsDirectoryObjects = async (
  directoryId: number,
  params?: { page?: number; pageSize?: number; text?: string; physicalId?: string; recordNumber?: string; objectType?: PhysicalObjectType; includeDescendants?: boolean; contentDateFrom?: string; contentDateTo?: string; description?: string; keywords?: string; contractParties?: string; fileId?: string; legacyBoxNumber?: string; offsiteId?: string; offsiteStorageId?: string; homeLocation?: string; currentLocation?: string; sort?: string },
): Promise<PagedResult<PhysicalObjectSearch>> =>
  apiClient.get<PagedResult<PhysicalObjectSearch>>(`${UNIFIED_BASE}/directories/${directoryId}/objects`, { params }).then(({ data }) => ({ ...data, items: data.items.map((item) => ({ ...item, objectType: normalizePhysicalObjectType(item.objectType as PhysicalObjectType | number) })) }));

export const createPhysicalRecordsDirectory = async (request: {
  parentDirectoryId: number;
  name: string;
  description?: string;
}): Promise<PhysicalRecordsDirectory> =>
  (await apiClient.post<PhysicalRecordsDirectory>(`${UNIFIED_BASE}/directories`, request)).data;

export const deletePhysicalRecordsDirectory = async (directoryId: number): Promise<void> => {
  await apiClient.delete(`${UNIFIED_BASE}/directories/${directoryId}`);
};

// ── Search (Phase 2) ─────────────────────────────────────────────────────

export interface SearchRequest {
  recordType?: "Box" | "File" | "";
  number?: string;
  name?: string;
  keywords?: string;
  description?: string;
  offsiteId?: string;
  offsiteStorageId?: string;
  contractParties?: string;
  departmentId?: number;
  locationId?: number;
  contentDateFrom?: string;
  contentDateTo?: string;
  createdBy?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
}

export interface SearchResult {
  recordType: "Box" | "File";
  id: number;
  number: string;
  name: string;
  departmentName: string;
  locationName: string;
  description?: string | null;
  keywords?: string | null;
  offsiteId?: string | null;
  offsiteStorageId?: string | null;
  contentStartDate?: string | null;
  contentEndDate?: string | null;
  createUser: string;
  createDt: string;
  boxId?: number | null;
  boxNumber?: string | null;
}

export const searchRecords = async (req: SearchRequest): Promise<SearchResult[]> =>
  (
    await apiClient.get<SearchResult[]>(`${RECORDS_BASE}/search`, {
      params: Object.fromEntries(Object.entries(req).filter(([, v]) => v !== "" && v !== undefined)),
    })
  ).data;

// ── Requests & Transfers (Phase 3) ───────────────────────────────────────

export interface PhysicalRecordsRequest {
  requestId: number;
  guid: string;
  boxId: number;
  boxNumber: string;
  boxName: string;
  requestedFileId?: number | null;
  requestedFileNumber?: string | null;
  requestedBy: string;
  requestedDt: string;
  status: "Requested" | "Borrowed" | "Returned" | "Cancelled";
  notes?: string | null;
}

export interface RequestBoxRequest {
  boxId: number;
  requestedFileId?: number | null;
  notes?: string;
}

const REQUESTS_BASE = "/api/physical-records/requests";

export const getRequests = async (status?: string): Promise<PhysicalRecordsRequest[]> =>
  (await apiClient.get<PhysicalRecordsRequest[]>(REQUESTS_BASE, { params: { status } })).data;
export const createRequest = async (req: RequestBoxRequest): Promise<PhysicalRecordsRequest> =>
  (await apiClient.post<PhysicalRecordsRequest>(REQUESTS_BASE, req)).data;
export const checkOutBox = async (requestId: number, toLocationId: number): Promise<void> => {
  await apiClient.post(`${REQUESTS_BASE}/${requestId}/check-out`, { toLocationId });
};
export const returnBox = async (requestId: number, returnLocationId: number): Promise<void> => {
  await apiClient.post(`${REQUESTS_BASE}/${requestId}/return`, null, { params: { returnLocationId } });
};

// ── Disposition report & scanned images & migration (Phase 4) ───────────

export interface DispositionReportFilter {
  eligibleOnly?: boolean;
  locationId?: number;
  retentionLabelId?: number;
  businessUnitId?: number;
  departmentId?: number;
  contentEndDateFrom?: string;
  contentEndDateTo?: string;
  status?: string;
}

export interface DispositionReportRow {
  dispositionSnapshotId: number;
  boxId: number;
  boxNumber: string;
  boxName: string;
  offsiteId?: string | null;
  locationName: string;
  retentionLabelName: string;
  businessUnitCode: string;
  departmentName: string;
  contentStartDate?: string | null;
  contentEndDate?: string | null;
  calculatedEligibleDate?: string | null;
  status: string;
  blockingReason?: string | null;
  approver?: string | null;
  approvedDate?: string | null;
  comment?: string | null;
  destructionOrderNumber?: string | null;
  certificateFilePath?: string | null;
}

export interface ScannedImageLink {
  scannedImageLinkId: number;
  guid: string;
  recordType: "Box" | "File";
  recordId: number;
  sharePointUrl?: string | null;
  legacyEcmUrl?: string | null;
}

export interface CsvImportRowResult {
  rowNumber: number;
  isValid: boolean;
  error?: string | null;
  rawValues: Record<string, string>;
}

export interface CsvImportPreviewResult {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  rows: CsvImportRowResult[];
}

export interface CsvImportCommitResult {
  importedCount: number;
  skippedCount: number;
  errors: string[];
}

const ADMIN_BASE = "/api/physical-records/admin";

export const getDispositionReport = async (filter: DispositionReportFilter): Promise<DispositionReportRow[]> =>
  (
    await apiClient.get<DispositionReportRow[]>(`${ADMIN_BASE}/disposition-report`, {
      params: Object.fromEntries(Object.entries(filter).filter(([, v]) => v !== "" && v !== undefined)),
    })
  ).data;

export const uploadCertificateOfDestruction = async (
  file: File,
  boxId: number,
  destructionOrderNumber?: string
): Promise<{ path: string }> => {
  const form = new FormData();
  form.append("file", file);
  form.append("boxId", String(boxId));
  if (destructionOrderNumber) form.append("destructionOrderNumber", destructionOrderNumber);
  return (await apiClient.post(`${ADMIN_BASE}/disposition-report/certificate`, form)).data;
};

export const getScannedImageLinks = async (recordType: "Box" | "File", recordId: number): Promise<ScannedImageLink[]> =>
  (await apiClient.get<ScannedImageLink[]>(`${ADMIN_BASE}/scanned-image-links`, { params: { recordType, recordId } })).data;

export const createScannedImageLink = async (
  recordType: "Box" | "File",
  recordId: number,
  sharePointUrl?: string,
  legacyEcmUrl?: string
): Promise<ScannedImageLink> =>
  (await apiClient.post<ScannedImageLink>(`${ADMIN_BASE}/scanned-image-links`, { recordType, recordId, sharePointUrl, legacyEcmUrl })).data;

export const previewImport = async (file: File, recordType: "Box" | "File"): Promise<CsvImportPreviewResult> => {
  const form = new FormData();
  form.append("file", file);
  form.append("recordType", recordType);
  return (await apiClient.post(`${ADMIN_BASE}/migration/preview`, form)).data;
};

export const commitImport = async (file: File, recordType: "Box" | "File"): Promise<CsvImportCommitResult> => {
  const form = new FormData();
  form.append("file", file);
  form.append("recordType", recordType);
  return (await apiClient.post(`${ADMIN_BASE}/migration/commit`, form)).data;
};




